<template>
  <header>
    <nav class="mx-auto flex max-w-7xl items-center justify-between py-4 px-6 lg:px-8" aria-label="Global">
      <!-- Logo -->
      <div class="flex lg:flex-1">
        <a href="/" class="-m-1.5 p-1.5" :title="siteConfig.title">
          <span class="sr-only">{{ siteConfig.title }}</span>
          <img class="h-16 lg:h-20 w-auto" src="/_resources/themes/abex/images/logo.svg" alt="Logo" />
        </a>
      </div>

      <!-- Mobile Hamburger + CTA -->
      <div class="flex items-center gap-4 lg:hidden">
        <a :href="siteConfig.callToAction.url" class="btn btn-primary text-sm font-semibold leading-6 text-white rounded-tl-2xl rounded-br-2xl">{{ siteConfig.callToAction.title }}</a>
        <button type="button" class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-white-700" @click="mobileMenuOpen = true">
          <span class="sr-only">Menu</span>
          <Bars3Icon class="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      <!-- Desktop Menu -->
      <PopoverGroup class="hidden lg:flex lg:justify-end lg:gap-x-8">
        <template v-for="(menuItem, index) in menuItems" :key="index">

        <Popover v-if="menuItem.Children.length" class="relative">
          <div
            @mouseenter="hoveredIndex = index"
            @mouseleave="hoveredIndex = null"
          >
            <!-- Parent link + icon -->
            <div class="flex items-center gap-x-1">
              <a :href="menuItem.Link" class="uppercase text-sm font-medium text-white-900 flex items-center">
                <div class="text-center">
                  {{ menuItem.Title }}<br>
                  <span v-if="menuItem.SecondaryMenuTitle" class="text-sm font-light">{{ menuItem.SecondaryMenuTitle }}</span>
                </div>
                <!-- Dropdown icon with bottom margin -->
                <ChevronDownIcon class="h-5 w-5 flex-none text-white-400 mb-8 ml-2" aria-hidden="true" />
              </a>
            </div>

            <!-- Children dropdown panel -->
            <transition
              enter-active-class="transition ease-out duration-200"
              enter-from-class="opacity-0 translate-y-1"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition ease-in duration-150"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 translate-y-1"
            >
              <PopoverPanel
                static
                v-show="hoveredIndex === index"
                class="absolute -left-16 top-full z-10 mt-3 w-screen max-w-xs overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-black/5"
              >
                <div class="p-4">
                  <!-- Parent link inside dropdown  -->
                  <div class="ml-4 p-2 font-medium text-black uppercase">
                    <a :href="menuItem.Link" class="block">
                      {{ menuItem.Title }}
                    </a>
                  </div>

                  <!-- Child links -->
                  <div v-for="child in menuItem.Children" :key="child.Title" class="ml-4 p-2 text-sm">
                    <a :href="child.Link" class="block text-black uppercase text-sm">
                      {{ child.Title }}
                    </a>
                  </div>
                </div>
              </PopoverPanel>
            </transition>
          </div>
        </Popover>

          <!-- Simple link if no children -->
          <a
            v-else
            :href="menuItem.Link"
            class="text-sm font-medium leading-6 text-white-900 uppercase"
          >
            <span>{{ menuItem.Title }}</span><br>
            <span v-if="menuItem.SecondaryMenuTitle" class="text-sm font-light">{{ menuItem.SecondaryMenuTitle }}</span>
          </a>
        </template>

        <!-- Desktop CTA -->
        <div class="hidden lg:flex lg:flex-1 lg:justify-end">
          <a :href="siteConfig.callToAction.url" class="btn btn-primary text-sm font-semibold leading-6 rounded-tl-2xl rounded-br-2xl h-10">{{ siteConfig.callToAction.title }}</a>
        </div>
      </PopoverGroup>
    </nav>

    <!-- Mobile Menu Dialog -->
    <Dialog class="lg:hidden" @close="mobileMenuOpen = false" :open="mobileMenuOpen">
      <div class="fixed inset-0 z-10" />
      <DialogPanel class="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-black px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-white-900/10">
        <div class="flex items-center justify-between">
          <a href="/" class="-m-1.5 p-1.5">
            <span class="sr-only">{{ siteConfig.title }}</span>
            <img class="h-20 w-auto" src="/_resources/themes/abex/images/logo.svg" alt="Logo" />
          </a>
          <button type="button" class="-m-2.5 rounded-md p-2.5 text-white-700" @click="mobileMenuOpen = false">
            <span class="sr-only">Close menu</span>
            <XMarkIcon class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div class="mt-6 flow-root">
          <div class="-my-6 divide-y divide-white-500/10 pt-6">
            <div class="space-y-2">
              <Disclosure v-for="(menuItem, index) in menuItems" :key="index" as="div" class="border-b" v-slot="{ open }">
                <template v-if="menuItem.Children.length">
                  <DisclosureButton class="flex w-full items-center justify-between pt-2 pb-2 text-base font-semibold leading-7 text-white-900">
                    <div class="text-left uppercase">
                      {{ menuItem.Title }}<br>
                      <span class="text-sm font-light">{{ menuItem.SecondaryMenuTitle }}</span>
                    </div>
                    <ChevronDownIcon :class="[open ? 'rotate-180' : '', 'h-5 w-5 flex-none']" />
                  </DisclosureButton>
                  <DisclosurePanel class="mt-2 space-y-2 pl-2">
                    <a :href="menuItem.Link" class="block uppercase py-2 border-b text-sm font-semibold leading-7 text-white-900">
                      {{ menuItem.Title }}
                    </a>
                    <DisclosureButton v-for="(child, i) in menuItem.Children" :key="child.Title" as="a" :href="child.Link" :class="['block py-2 text-sm font-semibold leading-7 text-white-900 uppercase', i !== menuItem.Children.length - 1 ? 'border-b' : '']">
                      {{ child.Title }}
                    </DisclosureButton>
                  </DisclosurePanel>
                </template>
                <a v-else :href="menuItem.Link" class="block uppercase py-2 text-base font-semibold leading-7 text-white-900">
                  {{ menuItem.Title }}<br>
                  <span class="text-sm font-light">{{ menuItem.SecondaryMenuTitle }}</span>
                </a>
              </Disclosure>
            </div>
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  </header>
</template>

<script setup>
import { ref } from 'vue'
import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from '@headlessui/vue'
import {
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/vue/24/outline'
import { ChevronDownIcon } from '@heroicons/vue/20/solid'

const props = defineProps({
  siteConfig: {
    type: Object,
    required: true
  },
  menuItems: {
    type: Array,
    required: true
  }
})

const mobileMenuOpen = ref(false)
const hoveredIndex = ref(null) // Track which desktop menu is hovered
</script>
